import { ApolloCache } from '@apollo/client';
import { IAbstractEntity } from '../schema/p4types';
import { cloneDeep } from '@apollo/client/utilities';
import { MutationUpdaterFunction } from '@apollo/client/core/types';
import { FetchResult } from '@apollo/client/link/core/types';

/**
 * Get a cache update function to directly update the instance/workspace queries
 * @param workspaceName - Name of the workspace query (I.e. 'products')
 * @param instanceName - Name of the instance query (I.e. 'product')
 * @param options - Additional options/metadata.
 *                - detailView - Effectively indicates whether to update the 'instance' cache (Typically a pass-through from the Workspace when using 'Form View').
 */
const useEntityCacheUpdate = <TData = any, TVariables = any>(
  workspaceName?: string,
  instanceName: string,
  options: { detailView?: boolean }
): MutationUpdaterFunction<TData, TVariables, unknown, any> => {
  return (cache: ApolloCache<any>, result: Omit<FetchResult<TData>, 'context'>) => {
    if (!result?.data) return;
    const updatedEntity = (result.data as any)[Object.keys(result.data)[0]]?.result;

    if (workspaceName) {
      cache.modify({
        fields: {
          [workspaceName]: (existingData: { edges?: Array<{ node?: IAbstractEntity }> }) => {
            const entityIndex = existingData.edges?.findIndex((edge) => edge?.node?.id === updatedEntity?.id) ?? -1;
            //typename check should be generally unnecessary, just being sure we don't make a mistake and cross-update.
            if (entityIndex >= 0 && existingData.edges?.[entityIndex]?.node?.__typename === updatedEntity?.__typename) {
              const newConnection = cloneDeep(existingData);
              const node = newConnection.edges![entityIndex].node;
              if (node) {
                //Shallow copy the fields (So we catch reference, description, etc), and only where the workspace already contains the field key.
                //This is to prevent apollo from busting the cache due to the different in selection criteria.
                Object.keys(node).forEach((key) => {
                  if (updatedEntity?.[key]?.__typename || Array.isArray(updatedEntity?.[key])) {
                    //Skip over objects, if the sub-selection on the instance vs workspace queries vary, it'll bust the cache and cause us a problem.
                    return;
                  }
                  (node as any)[key] = updatedEntity?.[key] ?? (node as any)[key];
                });
                return newConnection;
              }
            }
            return existingData;
          },
        },
      });
    }
    if (options?.detailView) {
      cache.modify({
        fields: {
          [instanceName]: (existingData: IAbstractEntity) => {
            if (
              updatedEntity &&
              updatedEntity.id === existingData?.id &&
              updatedEntity.__typename === existingData?.__typename
            ) {
              return updatedEntity;
            }
            return existingData;
          },
        },
      });
    }
  };
};

export default useEntityCacheUpdate;
